import React, { useMemo, useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SendIcon, AlertCircleIcon } from 'lucide-react';
import { FormSection, TextField, TextAreaField, DisplayField } from '../components/FormComponents';
import { mockRequisitions, mockStockEntries, mockProducts } from '../data/mockData';
export function StockIssueForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const requisition = mockRequisitions.find(r => r.id === id);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    remarks: ''
  });
  // Initialize issue quantities for each item
  const [issueQuantities, setIssueQuantities] = useState<Record<string, string>>(requisition?.items.reduce((acc, item) => ({
    ...acc,
    [item.id]: item.qty.toString()
  }), {}) || {});
  // Compute available stock for each product
  const stockBalances = useMemo(() => {
    const balances = new Map<string, number>();
    mockStockEntries.forEach(entry => {
      if (entry.status !== 'Completed') return;
      // We'll aggregate by product name since requisition items use product names
      const product = mockProducts.find(p => p.productCode === entry.productCode);
      if (!product) return;
      const current = balances.get(product.productName) || 0;
      const amount = entry.type === 'Stock In' ? entry.quantity : -entry.quantity;
      balances.set(product.productName, current + amount);
    });
    return balances;
  }, []);
  if (!requisition) {
    return <div className="p-8 text-center" data-id="element-2462">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-2463">
          Requisition not found
        </h2>
        <button onClick={() => navigate('/issue')} className="mt-4 text-indigo-600 hover:text-indigo-800" data-id="element-2464">
          Back to Issue List
        </button>
      </div>;
  }
  const handleQuantityChange = (itemId: string, value: string) => {
    setIssueQuantities(prev => ({
      ...prev,
      [itemId]: value
    }));
  };
  const handleSubmit = () => {
    // Validation
    let hasErrors = false;
    let errorMessages: string[] = [];
    requisition.items.forEach(item => {
      const issueQty = Number(issueQuantities[item.id]);
      const availableQty = stockBalances.get(item.item) || 0;
      if (isNaN(issueQty) || issueQty < 0) {
        hasErrors = true;
        errorMessages.push(`Invalid quantity for ${item.item}`);
      } else if (issueQty > availableQty) {
        hasErrors = true;
        errorMessages.push(`Cannot issue ${issueQty} of ${item.item}. Only ${availableQty} available in stock.`);
      }
    });
    if (hasErrors) {
      alert('Validation Errors:\n\n' + errorMessages.join('\n'));
      return;
    }
    alert('Stock issued successfully!');
    navigate('/issue');
  };
  return <div className="space-y-6 max-w-5xl mx-auto" data-id="element-2465">
      <div className="flex items-center gap-3" data-id="element-2466">
        <div className="p-3 bg-amber-100 rounded-lg" data-id="element-2467">
          <SendIcon className="h-6 w-6 text-amber-600" data-id="element-2468" />
        </div>
        <div data-id="element-2469">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2470">Issue Stock</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2471">
            Issue inventory against Requisition {requisition.reqNo}
          </p>
        </div>
      </div>

      <form className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-2472">
        <FormSection title="Requisition Details" data-id="element-2473">
          <DisplayField label="REQ No" value={requisition.reqNo} data-id="element-2474" />
          <DisplayField label="Department" value={requisition.department} data-id="element-2475" />
          <DisplayField label="Requested By" value={requisition.requestedBy} data-id="element-2476" />
          <DisplayField label="Required Date" value={requisition.requiredDate} data-id="element-2477" />
          <DisplayField label="Usage Type" value={requisition.reqFor} data-id="element-2478" />
          {requisition.employee && <DisplayField label="Employee" value={requisition.employee} data-id="element-2479" />}
        </FormSection>

        <FormSection title="Issue Details" data-id="element-2480">
          <TextField label="Issue Date" type="date" value={formData.date} onChange={e => setFormData({
          ...formData,
          date: e.target.value
        })} required data-id="element-2481" />
        </FormSection>

        <div className="mb-8" data-id="element-2482">
          <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-2483">
            Items to Issue
          </h3>

          <div className="overflow-x-auto" data-id="element-2484">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-2485">
              <thead className="bg-gray-50" data-id="element-2486">
                <tr data-id="element-2487">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2488">
                    Item
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-2489">
                    Purpose
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-2490">
                    Req. Qty
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-2491">
                    Available Stock
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32" data-id="element-2492">
                    Issue Qty
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2493">
                {requisition.items.map(item => {
                const availableQty = stockBalances.get(item.item) || 0;
                const issueQty = Number(issueQuantities[item.id]) || 0;
                const isOverStock = issueQty > availableQty;
                return <tr key={item.id} className={isOverStock ? 'bg-red-50' : ''} data-id="element-2494">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2495">
                        {item.item}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500" data-id="element-2496">
                        {item.purpose}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-900" data-id="element-2497">
                        {item.qty}
                      </td>
                      <td className="px-4 py-3 text-sm text-center" data-id="element-2498">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${availableQty > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-2499">
                          {availableQty}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-2500">
                        <input type="number" min="0" max={availableQty} className={`block w-full rounded-md shadow-sm sm:text-sm border p-1.5 text-center ${isOverStock ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-amber-500 focus:border-amber-500'}`} value={issueQuantities[item.id]} onChange={e => handleQuantityChange(item.id, e.target.value)} data-id="element-2501" />
                        {isOverStock && <p className="mt-1 text-xs text-red-600 flex items-center justify-center" data-id="element-2502">
                            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-2503" /> Exceeds
                            stock
                          </p>}
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>

        <FormSection title="Additional Information" data-id="element-2504">
          <div className="md:col-span-2" data-id="element-2505">
            <TextAreaField label="Remarks" value={formData.remarks} onChange={e => setFormData({
            ...formData,
            remarks: e.target.value
          })} rows={3} placeholder="Reason for issuing stock, condition notes, etc..." data-id="element-2506" />
          </div>
        </FormSection>

        <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3" data-id="element-2507">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2508">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700" data-id="element-2509">
            Issue Stock
          </button>
        </div>
      </form>
    </div>;
}